import type {
  DsdEditorNoteReferenceCheck,
  DsdEditorValueLink,
} from "@/apis/dsd-editor/types";
import type {
  DsdValueLinkVerdict,
  PlainDsdNoteReferenceStatus,
} from "@/types/plain-dsd-table";

/**
 * 약한 상태 → 센 상태 순. 순서가 곧 우선순위다.
 * 연결 없음(unlinked)이 가장 약하다 — 정보가 없다는 뜻이라 다른 판정을 가리면 안 된다.
 */
export const NOTE_REFERENCE_DISPLAY_STATUS_ORDER: readonly PlainDsdNoteReferenceStatus[] =
  ["unlinked", "confirmed", "unconfirmed", "reference_missing"];

/** 한 셀에 여러 판정이 겹칠 때 남길 상태. 같은 세기면 먼저 잡힌 쪽(a)을 유지한다 */
export const strongerNoteReferenceDisplayStatus = (
  a: PlainDsdNoteReferenceStatus,
  b: PlainDsdNoteReferenceStatus,
) =>
  NOTE_REFERENCE_DISPLAY_STATUS_ORDER.indexOf(a) >=
  NOTE_REFERENCE_DISPLAY_STATUS_ORDER.indexOf(b)
    ? a
    : b;

/** 후보(주석) 셀 색 — 매칭된 본문 셀의 상태를 그대로 물려받는다 */
export type DsdNoteReferenceCandidateTone = "positive" | "caution" | "negative";

/**
 * Set으로 예외만 열거하면 상태가 늘 때 조용히 초록으로 떨어진다(reference_missing 사례).
 * 전 상태를 적는 Record로 두어 새 상태를 넣는 순간 컴파일이 깨지게 한다.
 */
export const NOTE_REFERENCE_CANDIDATE_TONE: Record<
  PlainDsdNoteReferenceStatus,
  DsdNoteReferenceCandidateTone
> = {
  confirmed: "positive",
  // 연결 없음 — 본문 셀은 색이 없고 후보는 값이 맞은 셀이다
  unlinked: "positive",
  unconfirmed: "caution",
  // reference_missing은 값이 맞은 후보가 반드시 있다
  reference_missing: "negative",
};

const CANDIDATE_TONE_PRIORITY: Record<DsdNoteReferenceCandidateTone, number> = {
  positive: 0,
  caution: 1,
  negative: 2,
};

/** 한 셀이 여러 match의 후보면 더 센 색이 남는다 (빨강 > 노랑 > 초록) */
export const strongerCandidateTone = (
  a: DsdNoteReferenceCandidateTone,
  b: DsdNoteReferenceCandidateTone,
) => (CANDIDATE_TONE_PRIORITY[a] >= CANDIDATE_TONE_PRIORITY[b] ? a : b);

/**
 * 연결 하나의 판정 — 백엔드는 근거 셋만 보내고 조합은 여기 하나에서 한다.
 * 빨강은 이 셀이 계정명으로도 뽑히고 값도 같은데 주기에 없을 때만이다. 주기가 있으면 근거 하나로도 초록.
 */
export const valueLinkVerdictOf = (
  link: Pick<DsdEditorValueLink, "textMatched" | "valueMatched" | "referenced">,
): DsdValueLinkVerdict => {
  if (link.textMatched && link.valueMatched)
    return link.referenced ? "confirmed" : "reference_missing";
  return link.referenced ? "confirmed" : "unconfirmed";
};

/**
 * 주기에만 적히고 상대 셀이 없는 주석 중 화면이 알릴 것 — 셀 상태(노랑)와 패널 행이 이 한 목록을 쓴다.
 * 주석으로 해석되지 않은 번호(chunkId 없음)는 가리킬 주석이 없어 뺀다.
 */
export const getShownUnlinkedReferences = (
  check: Pick<DsdEditorNoteReferenceCheck, "unlinkedReferences">,
) => check.unlinkedReferences.filter((reference) => reference.chunkId);

/**
 * 셀의 사실 상태 — 연결마다의 판정(valueLinkVerdictOf) 중 가장 센 것. 주기에만 적히고 상대 셀이 없는 주석
 * (getShownUnlinkedReferences)은 노랑으로 알린다 — 패널이 같은 목록으로 노랑 행을 그린다. 연결이 없으면 unlinked — 주석으로 해석되지 않은 주기 번호
 * (noteReferences[].chunkId == null)도 아직 따로 표시하지 않는다.
 *
 * 패널 문구의 기준이라 검토 판단은 반영하지 않는다. 화면의 셀 색은 후보 카드와 검토 판단으로 다시 정한다(cell-verdict.ts).
 */
export const getNoteReferenceDisplayStatus = (
  check: DsdEditorNoteReferenceCheck,
): PlainDsdNoteReferenceStatus => {
  const verdicts = check.valueLinks.map(valueLinkVerdictOf);
  if (getShownUnlinkedReferences(check).length > 0)
    verdicts.push("unconfirmed");
  // unlinked 가 가장 약해서 연결이 없으면 그대로 남는다
  return verdicts.reduce<PlainDsdNoteReferenceStatus>(
    strongerNoteReferenceDisplayStatus,
    "unlinked",
  );
};
